import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { CreateDisc } from "@babylonjs/core/Meshes/Builders/discBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { CROWD, PickupKind, TICK_RATE, type PickupState } from "@arena/shared";

/** Pixel art of the objects, one string per row (palette letters, "." = clear). */
const ART: Record<number, { rows: string[]; palette: Record<string, string>; glow: string }> = {
  [PickupKind.Apple]: {
    rows: ["...b...", "...bg..", ".rr.rr.", "rrrrrrr", "rwrrrrr", "rrrrrrr", ".rrrrr.", "..r.r.."],
    palette: { r: "#c9252f", g: "#56a83c", b: "#5a3a1a", w: "#ffb3a8" },
    glow: "#ffd24a",
  },
  [PickupKind.Amphora]: {
    rows: ["..OOO..", "..ooo..", "o.ooo.o", "oOOOOOo", ".OOOOO.", "OOdddOO", "OOOOOOO", ".OOOOO.", "..OOO..", "...O..."],
    palette: { O: "#c8743a", o: "#9a5228", d: "#1e1410" },
    glow: "#ff7a4a",
  },
};

/** Where in the stands the throw starts: further out than the wall, up high. */
const THROW_DISTANCE = 22;
const THROW_HEIGHT = 9;

/**
 * An object thrown by the crowd (D38): it flies in from the stands in an arc
 * while its shadow on the sand grows (the warning of where it lands), then
 * floats and glows until someone takes it.
 */
export class PickupView {
  private readonly sprite: Mesh;
  private readonly shadow: Mesh;
  private readonly ring: Mesh;
  private readonly ringMaterial: StandardMaterial;
  private readonly materials: StandardMaterial[] = [];
  private readonly texture: DynamicTexture;
  private readonly from: { x: number; y: number; z: number };
  private readonly state: PickupState;
  private progress: number;
  private time = 0;

  constructor(scene: Scene, state: PickupState) {
    this.state = state;
    const art = ART[state.kind] ?? ART[PickupKind.Apple]!;
    this.texture = pixelTexture(scene, art.rows, art.palette);
    const material = new StandardMaterial("pickup", scene);
    material.diffuseTexture = this.texture;
    material.useAlphaFromDiffuseTexture = true;
    material.emissiveColor = new Color3(0.62, 0.62, 0.62);
    material.disableLighting = true;
    material.backFaceCulling = false;
    this.materials.push(material);
    this.sprite = CreatePlane("pickup", { width: 0.62, height: 0.62 * (art.rows.length / art.rows[0]!.length) }, scene);
    this.sprite.billboardMode = Mesh.BILLBOARDMODE_Y;
    this.sprite.material = material;
    this.sprite.isPickable = false;

    const shadowMaterial = new StandardMaterial("pickup-shadow", scene);
    shadowMaterial.diffuseColor = Color3.Black();
    shadowMaterial.specularColor = Color3.Black();
    shadowMaterial.alpha = 0.4;
    this.materials.push(shadowMaterial);
    this.shadow = CreateDisc("pickup-shadow", { radius: 0.32, tessellation: 16 }, scene);
    this.shadow.rotation.x = Math.PI / 2;
    this.shadow.material = shadowMaterial;
    this.shadow.isPickable = false;

    this.ringMaterial = new StandardMaterial("pickup-ring", scene);
    this.ringMaterial.disableLighting = true;
    this.ringMaterial.emissiveColor = Color3.FromHexString(art.glow);
    this.ringMaterial.alpha = 0;
    this.materials.push(this.ringMaterial);
    this.ring = CreateDisc("pickup-ring", { radius: 0.6, tessellation: 24 }, scene);
    this.ring.rotation.x = Math.PI / 2;
    this.ring.material = this.ringMaterial;
    this.ring.isPickable = false;

    const len = Math.hypot(state.x, state.z) || 1;
    this.from = { x: (state.x / len) * THROW_DISTANCE, y: THROW_HEIGHT, z: (state.z / len) * THROW_DISTANCE * 0.75 };
    this.progress = 1 - state.fall / CROWD.fallTicks;
  }

  update(dt: number): void {
    this.time += dt;
    const s = this.state;
    // Smooth between patches, never behind the server's own count.
    const served = 1 - s.fall / CROWD.fallTicks;
    this.progress = Math.min(1, Math.max(served, this.progress + dt / (CROWD.fallTicks / TICK_RATE)));
    const p = this.progress;
    const landed = p >= 1;
    const x = this.from.x + (s.x - this.from.x) * p;
    const z = this.from.z + (s.z - this.from.z) * p;
    const arc = 4 * 3 * p * (1 - p);
    const y = landed ? s.y + 0.45 + Math.sin(this.time * 3) * 0.08 : this.from.y + (s.y + 0.4 - this.from.y) * p + arc;
    this.sprite.position.set(x, y, z);
    this.shadow.position.set(s.x, s.y + 0.012, s.z);
    const size = landed ? 1 : 0.4 + 0.9 * p;
    this.shadow.scaling.set(size, size, 1);
    (this.shadow.material as StandardMaterial).alpha = landed ? 0.35 : 0.2 + 0.4 * p;
    this.ring.position.set(s.x, s.y + 0.01, s.z);
    this.ringMaterial.alpha = landed ? 0.18 + 0.14 * Math.sin(this.time * 5) : 0;
  }

  dispose(): void {
    this.sprite.dispose();
    this.shadow.dispose();
    this.ring.dispose();
    for (const m of this.materials) m.dispose();
    this.texture.dispose();
  }
}

function pixelTexture(scene: Scene, rows: string[], palette: Record<string, string>): DynamicTexture {
  const w = rows[0]!.length;
  const h = rows.length;
  const texture = new DynamicTexture("pickup-art", { width: w, height: h }, scene, false, Texture.NEAREST_SAMPLINGMODE);
  const g = texture.getContext() as unknown as CanvasRenderingContext2D;
  g.clearRect(0, 0, w, h);
  rows.forEach((row, y) =>
    [...row].forEach((ch, x) => {
      const color = palette[ch];
      if (!color) return;
      g.fillStyle = color;
      g.fillRect(x, y, 1, 1);
    }),
  );
  texture.update(false);
  texture.hasAlpha = true;
  return texture;
}
